import { acceptHMRUpdate, defineStore } from 'pinia'
import { toRaw } from 'vue'
import { useMaps } from './maps'
import { useDrawingStore } from './drawing'
import { postCorrectBorder, postCheckBorder } from '@/scripts/fieldsPlanning'
import { useSvgStore } from './svg'
import i18n from '@/i18n'
import { capitalize, removeUnderline } from '@/helpers/stringHelper'
import { geoJsonRingToOpenPath } from '@/helpers/geometryHelper'

export const useFieldProblemsStore = defineStore('fieldProblems', {
    state: () => ({
        problems: {
            type: [],
            coord: [],
            ignorable: [],
        },
        tempOuterBorder: null,
        markers: [],
        currentPage: 0,
        infoWindow: null,
        loading: false,
        allIgnorableSkipped: false,
    }),
    actions: {
        checkFieldProblems(response) {
            this.clearMarkers()

            const features = response?.problems?.features
            if (!features?.length) return

            for (const problem of features) {
                if (!problem.properties || !problem.geometry) continue
                if (this.allIgnorableSkipped && problem.properties.ignorable) continue

                this.problems.type.push(problem.properties.problem_type || null)
                this.problems.coord.push(problem.geometry.coordinates || null)
                this.problems.ignorable.push(problem.properties.ignorable || false)
            }
            this.showMarkersOnMap()
        },
        async checkBorder() {
            const drawingStore = useDrawingStore()
            this.loading = true
            try {
                const res = await postCheckBorder({
                    outer_border: drawingStore.field.requestHeadlandData.outer_border,
                })

                this.checkFieldProblems(res)

                if (this.markers.length) {
                    drawingStore.steps = 0
                }
            } catch (error) {
                console.error('Error in checkBorder', error)
            } finally {
                this.loading = false
            }
        },
        getIgnorable() {
            return this.problems.ignorable[this.currentPage]
        },
        clearMarkers() {
            this.markers.forEach((marker) => {
                if (toRaw(marker)) {
                    toRaw(marker).map = null
                }
            })

            this.markers = []
            this.tempOuterBorder = null
            this.currentPage = 0
            
            this.problems = {
                type: [],
                coord: [],
                ignorable: []
            }

            this.removeClickListeners()
        },
        async addClickListeners() {
            this.removeClickListeners()

            const mapStore = useMaps()
            this.markers.forEach((marker, index) => {
                if (marker) {
                    mapStore.google.maps.event.addListener(marker, 'click', async () => {
                        this.currentPage = index
                        await this.correctBorder(this.currentPage)
                        await this.seeProblemMarker()
                    })
                }
            })
        },
        removeClickListeners() {
            const mapStore = useMaps()
            
            this.markers.forEach(marker => {
                if (marker) {
                    mapStore.google.maps.event.clearListeners(marker, 'click')
                }
            })
        },
        async seeProblemMarker() {
            if (this.currentPage >= this.markers.length) {
                this.currentPage = Math.max(0, this.markers.length - 1);
            }

            if (this.currentPage < 0) this.currentPage = 0;

            if (this.markers.length > 0) {
                const newMarker = this.markers[this.currentPage];
                if (!newMarker) {
                    console.warn('seeProblemMarker: newMarker is undefined at', this.currentPage);
                    return;
                }

                this.centerToMarker(newMarker)
                this.showInfoWindow(newMarker, this.currentPage)

                const drawingStore = useDrawingStore()
                // set fieldValidationError in function of problem type
                drawingStore.fieldValidationError = this.problems.type[this.currentPage] === 'self_intersection'

                const svgStore = useSvgStore()
                svgStore.updateSvgCenter({
                    lat: newMarker.position.lat,
                    lng: newMarker.position.lng
                })
                svgStore.resetFixedBoundingBoxAndSVG()
                await this.addClickListeners()
            }
        },
        showInfoWindow(marker, index) {
            if (!marker) {
                console.warn('showInfoWindow: marker is undefined at index', index);
                return;
            }
            if (!this.problems.type[index] || !this.problems.coord[index]) {
                console.warn('showInfoWindow: problem data missing at index', index, this.problems.type[index], this.problems.coord[index]);
                return;
            }
            if (this.infoWindow) {
                this.infoWindow.close()
            }

            const problemType = this.problems.type[index] || 'Unknown'
            const coordinates = this.problems.coord[index] 
                ? `${this.problems.coord[index][1]}, ${this.problems.coord[index][0]}`
                : 'Unknown'
            const ignorable = this.problems.ignorable[index]

            const mapStore = useMaps()
            this.infoWindow = new mapStore.google.maps.InfoWindow({
                content: `
                    <div style="display: flex; width: 100%; justify-content: center;">
                        <span style="font-weight: 600; color: #2c3e50; width: fit-content;">
                            ${i18n.global.t('Problem')}:  ${index + 1} / ${this.problems.coord.length}
                        </span>
                    </div>
                    <div style="padding: 10px; min-width: 200px; font-family: Arial, sans-serif;">
                        <div style="margin-bottom: 8px;">
                            <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Problem Type')}:</span>
                            <span style="margin-left: 4px; color: #e74c3c;">
                                ${i18n.global.t(capitalize(removeUnderline(problemType)))}
                            </span>
                        </div>
                        <div style="margin-bottom: 8px;">
                            <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Coordinates')}:</span>
                            <span style="margin-left: 4px; color: #2c3e50; font-family: monospace; font-size: 14px;">
                                ${coordinates}
                            </span>
                        </div>
                        <div>
                            <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Ignorable')}:</span>
                            <span style="margin-left: 4px; color: ${ignorable ? '#27ae60' : '#e74c3c'}">
                                ${ignorable ? i18n.global.t('Yes') : i18n.global.t('No')}
                            </span>
                        </div>
                    </div>
                `,
                position: marker.position
            })

            this.infoWindow.open(mapStore.map, marker)
        },
        showMarkersOnMap() {
            const mapStore = useMaps()
            
            if (!this.problems.coord || !Array.isArray(this.problems.coord)) {
                return
            }

            // Create markers directly from the problems data
            for (let i = 0; i < this.problems.coord.length; i++) {
                const coord = this.problems.coord[i]
                if (coord && coord.length >= 2) {
                    const position = new (toRaw(mapStore.google).maps.LatLng)(coord[1], coord[0])
                    
                    const marker = new mapStore.google.maps.marker.AdvancedMarkerElement({
                        position: position,
                        map: toRaw(mapStore.map),
                        title: this.problems.type[i] || 'Problem Marker'
                    })
                    
                    this.markers.push(marker)
                }
            }
        },
        centerToMarker(marker) {
            if (!marker) return

            const mapStore = useMaps()

            const position = marker.getPosition ? marker.getPosition() : marker.position
            mapStore.map.setCenter(position)
        },
        removeProblem(index) {
            // Remove marker from map
            if (this.markers[index]) {
                this.markers[index].map = null
                this.markers.splice(index, 1)
            }
            // Remove problem data
            if (this.problems) {
                if (this.problems.type) this.problems.type.splice(index, 1)
                if (this.problems.coord) this.problems.coord.splice(index, 1)
                if (this.problems.ignorable) this.problems.ignorable.splice(index, 1)
            }
            // Adjust currentPage if needed
            if (this.currentPage >= this.markers.length) {
                this.currentPage = Math.max(0, this.markers.length - 1);
            }
            if (this.currentPage < 0) this.currentPage = 0;
        },
        async correctBorder(currentPage) {
            const drawingStore = useDrawingStore()

            this.tempOuterBorder = null
            this.loading = true
            // Create a new problem request with only the current page's problem
            const currentProblem = {
                type: "FeatureCollection",
                features: [{
                    id: "0",
                    type: "Feature",
                    properties: {
                        field_geom_type: "problem_area",
                        problem_type: this.problems.type[currentPage],
                        ignorable: this.problems.ignorable[currentPage]
                    },
                    geometry: {
                        type: "Point",
                        coordinates: this.problems.coord[currentPage]
                    }
                }]
            }
            
            try {
                const res = await postCorrectBorder({
                    outer_border: drawingStore.field.requestHeadlandData.outer_border,
                    problems: currentProblem
                })

                if (!res) {
                    throw new Error('No response from postCorrectBorder')
                }

                this.tempOuterBorder = res

                this.loading = false
                return res
            } catch (error) {
                console.error('Error in correctBorder', error)
                this.loading = false
                return null
            }
        },
        applySuggestion() {
            const drawingStore = useDrawingStore()
            const mapStore = useMaps()

            // Extract the coordinates from the tempOuterBorder
            const feature = this.tempOuterBorder.outer_border.features[0]
            const coords = geoJsonRingToOpenPath(feature.geometry.coordinates[0]).map(
                p => new mapStore.google.maps.LatLng(p.lat, p.lng)
            )

            // Update the outerBorder in the drawingStore
            if (drawingStore.field.shapes && drawingStore.field.shapes[0]) {
                const path = new mapStore.google.maps.MVCArray(coords)
                drawingStore.field.shapes[0].setPath(path)
                drawingStore.removeHandleUpdateListeners(drawingStore.field.shapes[0])
                drawingStore.addHandleUpdateListeners(drawingStore.field.shapes[0])
                // Clear all inner rings (obstacles) from shapes[0], keep only the outer border
                const mainPolygon = drawingStore.field.shapes[0]
                const paths = mainPolygon.getPaths()
                while (paths.getLength() > 1) {
                    paths.pop()
                }
            }

            // Update obstacles from tempOuterBorder if they exist
            if (feature.geometry.coordinates.length > 1) {
                // Extract obstacle coordinates (inner rings) from the corrected geometry
                const obstacleCoords = feature.geometry.coordinates.slice(1)
                
                // Update each obstacle Polygon separately (cutouts)
                for (let i = 0; i < obstacleCoords.length && i < drawingStore.field.cutouts.length; i++) {
                    const obstacle = drawingStore.field.cutouts[i]
                    const correctedCoords = geoJsonRingToOpenPath(obstacleCoords[i]).map(
                        p => new mapStore.google.maps.LatLng(p.lat, p.lng)
                    )
                    const path = new mapStore.google.maps.MVCArray(correctedCoords)
                    toRaw(obstacle).setPath(path)
                    drawingStore.removeHandleUpdateListeners(obstacle)
                    drawingStore.addHandleUpdateListeners(obstacle)
                }
                // Remove extra obstacles from the map and array
                while (drawingStore.field.cutouts.length > obstacleCoords.length) {
                    const removed = drawingStore.field.cutouts.pop()
                    if (removed) toRaw(removed).setMap(null)
                }
            } else {
                // If there are no inner rings, remove all cutouts
                while (drawingStore.field.cutouts.length > 0) {
                    const removed = drawingStore.field.cutouts.pop()
                    if (removed) toRaw(removed).setMap(null)
                }
            }

            this.removeClickListeners()
            this.removeProblem(this.currentPage)

            // Update current page if we're at the last marker
            if (this.currentPage >= this.markers.length) {
                this.currentPage = Math.max(0, this.markers.length - 1)
            }
        },
        skipAllProblems() {
            this.allIgnorableSkipped = true
            this.removeClickListeners()
            for (let i = this.problems.ignorable.length - 1; i >= 0; i--) {
                if (this.problems.ignorable[i]) {
                    this.removeProblem(i)
                }
            }
        }
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFieldProblemsStore, import.meta.hot))
}